"use client";

import { useMemo, useState } from "react";
import { HiClipboardCopy, HiCheck, FaYoutube } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const YOUTUBE_TAG_CHAR_LIMIT = 500;

type Niche =
  | "gaming"
  | "tech"
  | "fitness"
  | "vlog"
  | "beauty"
  | "music"
  | "education"
  | "business"
  | "food"
  | "travel";

const NICHE_LABELS: Record<Niche, string> = {
  gaming: "Gaming",
  tech: "Tech & Reviews",
  fitness: "Fitness & Wellness",
  vlog: "Vlog & Lifestyle",
  beauty: "Beauty & Fashion",
  music: "Music",
  education: "Education",
  business: "Business & Finance",
  food: "Food & Cooking",
  travel: "Travel",
};

const NICHE_TAGS: Record<Niche, string[]> = {
  gaming: [
    "gameplay",
    "gaming",
    "let's play",
    "walkthrough",
    "speedrun",
    "tips and tricks",
    "tutorial",
    "review",
    "highlights",
    "esports",
    "indie game",
    "PC gaming",
    "console gaming",
    "boss fight",
    "ranked",
    "competitive",
    "mod",
    "best moments",
    "funny moments",
    "fail compilation",
  ],
  tech: [
    "tech review",
    "unboxing",
    "tech news",
    "gadgets",
    "smartphone",
    "laptop review",
    "comparison",
    "vs",
    "hands on",
    "first impressions",
    "long term review",
    "best of 2026",
    "should you buy",
    "worth it",
    "specs",
    "benchmarks",
    "real world test",
    "setup",
    "build",
    "deep dive",
  ],
  fitness: [
    "workout",
    "fitness",
    "home workout",
    "gym",
    "strength training",
    "cardio",
    "weight loss",
    "fat loss",
    "muscle building",
    "abs workout",
    "leg day",
    "transformation",
    "fitness journey",
    "fitness motivation",
    "no equipment",
    "full body",
    "HIIT",
    "stretching",
    "mobility",
    "meal prep",
  ],
  vlog: [
    "vlog",
    "daily vlog",
    "day in my life",
    "lifestyle",
    "morning routine",
    "evening routine",
    "weekly vlog",
    "what I eat in a day",
    "productivity",
    "self improvement",
    "habits",
    "minimalism",
    "slow living",
    "aesthetic",
    "cozy",
    "behind the scenes",
    "honest",
    "realistic",
    "raw",
    "candid",
  ],
  beauty: [
    "makeup tutorial",
    "skincare routine",
    "get ready with me",
    "GRWM",
    "beauty",
    "makeup",
    "skincare",
    "haircare",
    "product review",
    "haul",
    "first impressions",
    "drugstore",
    "high end",
    "everyday makeup",
    "natural makeup",
    "bold makeup",
    "before and after",
    "tips",
    "hacks",
    "favorites",
  ],
  music: [
    "music",
    "cover",
    "original song",
    "songwriting",
    "music production",
    "behind the song",
    "lyric video",
    "official video",
    "acoustic",
    "live performance",
    "studio session",
    "remix",
    "mashup",
    "tutorial",
    "tab",
    "chord",
    "how to play",
    "music theory",
    "gear",
    "DAW",
  ],
  education: [
    "tutorial",
    "explained",
    "learn",
    "how to",
    "beginner guide",
    "advanced",
    "for beginners",
    "in 10 minutes",
    "in 5 minutes",
    "course",
    "lesson",
    "step by step",
    "fundamentals",
    "deep dive",
    "case study",
    "real example",
    "common mistakes",
    "best practices",
    "comparison",
    "FAQ",
  ],
  business: [
    "business",
    "entrepreneur",
    "startup",
    "personal finance",
    "investing",
    "side hustle",
    "passive income",
    "make money online",
    "marketing",
    "sales",
    "leadership",
    "productivity",
    "case study",
    "interview",
    "business model",
    "growth",
    "scaling",
    "lessons learned",
    "honest review",
    "real numbers",
  ],
  food: [
    "recipe",
    "cooking",
    "easy recipe",
    "quick recipe",
    "5 ingredient",
    "30 minute meal",
    "healthy recipe",
    "comfort food",
    "weeknight dinner",
    "meal prep",
    "vegetarian",
    "vegan",
    "gluten free",
    "low carb",
    "dessert",
    "baking",
    "cooking tutorial",
    "kitchen tips",
    "ingredient swap",
    "from scratch",
  ],
  travel: [
    "travel",
    "travel vlog",
    "travel guide",
    "things to do",
    "itinerary",
    "budget travel",
    "luxury travel",
    "solo travel",
    "couple travel",
    "family travel",
    "first time visiting",
    "hidden gems",
    "tourist traps",
    "honest review",
    "where to stay",
    "where to eat",
    "best photo spots",
    "day trip",
    "weekend trip",
    "off the beaten path",
  ],
};

function normalizeKeyword(s: string): string {
  return s.trim().replace(/\s+/g, " ").toLowerCase();
}

function generateTags(input: string, niche: Niche): string[] {
  const seeds = input.split(/[,\n]/).map(normalizeKeyword).filter(Boolean);

  const tags = new Set<string>();

  // Lead with the user's exact seeds (highest relevance signal for YouTube)
  for (const seed of seeds) tags.add(seed);

  // Multi-word combinations of seeds with niche modifiers
  const nicheTags = NICHE_TAGS[niche];
  for (const seed of seeds) {
    for (const modifier of nicheTags.slice(0, 8)) {
      const combo = `${seed} ${modifier}`;
      if (combo.length <= 30) tags.add(combo);
      const inverted = `${modifier} ${seed}`;
      if (inverted.length <= 30) tags.add(inverted);
    }
  }

  // Then niche base tags
  for (const tag of nicheTags) tags.add(tag);

  // Enforce YouTube's 500-char total limit (joined with commas + space)
  const ordered = Array.from(tags);
  const accepted: string[] = [];
  let runningLen = 0;
  for (const tag of ordered) {
    const cost = tag.length + (accepted.length === 0 ? 0 : 2); // ", "
    if (runningLen + cost > YOUTUBE_TAG_CHAR_LIMIT) break;
    accepted.push(tag);
    runningLen += cost;
  }
  return accepted;
}

const FAQ_ITEMS = [
  {
    question: "What's YouTube's 500-character tags limit?",
    answer:
      "YouTube caps the combined length of all tags on a video at 500 characters (including commas and spaces between tags). The tool stops adding tags before that limit so the full list will fit when you paste it into the video's tag field.",
  },
  {
    question: "Do YouTube tags actually help with ranking?",
    answer:
      "Less than they used to. YouTube has stated tags are 'minimal' for video discovery, with the primary use being to help fix common misspellings. They still matter for unusual topic terms and niche language, and they're free to fill in — so they remain a small-but-real lever. Focus your time on title, description, and thumbnail first.",
  },
  {
    question: "Should I copy the tags exactly or edit them first?",
    answer:
      "Edit. The tool generates a strong baseline of relevant tags from your topic, but you'll add value by replacing generic ones with channel-specific terms (your brand name, your series name, recurring character names, etc.) — those are tags only you can provide that distinguish your video.",
  },
  {
    question: "Are tags the same as hashtags?",
    answer:
      "No. Tags are entered in the video's tag field and are not publicly visible. Hashtags are written in the video's title or description with a # prefix and ARE publicly visible — they appear above the title and link to a hashtag results page. Use tags for SEO, hashtags for category navigation.",
  },
  {
    question: "Why are some tags multi-word?",
    answer:
      "Multi-word tags ('beginner guide', 'home workout', 'travel vlog') signal a specific topic intent more clearly than a single word would. They also tend to match longer search queries, which often have less competition than the single-word terms.",
  },
];

export default function YoutubeTagGeneratorClient() {
  const [topic, setTopic] = useState("");
  const [niche, setNiche] = useState<Niche | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const tags = useMemo(() => {
    if (!niche || !topic.trim()) return [];
    return generateTags(topic, niche);
  }, [topic, niche]);

  const joined = tags.join(", ");
  const totalChars = joined.length;

  const copy = async (content: string, key: string) => {
    await navigator.clipboard.writeText(content);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="YouTube Tag Generator"
        description="Generate 30+ relevant YouTube tags from your video topic or title. Stays under YouTube's 500-character tags limit."
        url="https://plugmonkey.xyz/tool/youtube-tag-generator/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "YouTube Tag Generator",
            url: "https://plugmonkey.xyz/tool/youtube-tag-generator/",
          },
        ]}
        faq={FAQ_ITEMS}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-red-500/10 rounded-full text-red-500">
          <FaYoutube className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">YouTube Tag Generator</h1>
          <p className="text-muted-foreground mt-1">
            Generate niche-tuned tags that stay under YouTube&apos;s 500-character limit.
          </p>
        </div>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Video Topic or Title</CardTitle>
          <CardDescription>
            Type the main topic, comma-separated keywords, or paste the video title.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            id="video-topic"
            name="video-topic"
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. iPhone 17 review, beginner Python tutorial, full body home workout"
            className="w-full px-4 py-3 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label="Video topic or title"
          />

          <div>
            <div className="text-sm font-semibold mb-2">Niche</div>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(NICHE_LABELS) as Niche[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setNiche(key)}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    niche === key
                      ? "border-primary bg-primary text-white"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {NICHE_LABELS[key]}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {tags.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
            <div>
              <CardTitle>
                {tags.length} Tags · {totalChars}/{YOUTUBE_TAG_CHAR_LIMIT} chars
              </CardTitle>
              <CardDescription className="mt-1">
                Click any tag to copy it. Hit &quot;Copy all&quot; for the full comma-separated
                list.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => copy(joined, "all")}>
              {copied === "all" ? (
                <HiCheck className="w-4 h-4" />
              ) : (
                <HiClipboardCopy className="w-4 h-4" />
              )}
              {copied === "all" ? "Copied" : "Copy all"}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => copy(tag, tag)}
                  className="px-3 py-1.5 rounded-full border border-border bg-muted/40 hover:bg-muted text-sm transition-colors font-mono"
                >
                  {copied === tag ? "✓ " : ""}
                  {tag}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mt-16">
        <ToolsCustomDevCTA />
      </div>

      <div className="mt-16">
        <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
        <UnifiedFAQ items={FAQ_ITEMS} showSupportLink={false} className="space-y-4" />
      </div>
    </div>
  );
}
